<template>
  <main>
    <LoadingOverlay :loading="isLoading" />

    <div class="view-main-container">
      <div class="top">
        <div>
          <div class="d-flex flex-row justify-content-center mt-5">
            <img
              :src="useAsset('img/core-app-onboarding/id_verify.svg')"
              :width="200"
            />
          </div>
          <div class="d-flex flex-row justify-content-center mb-5">
            <h1 class="text-center fs-2">
              {{ t("CORE_APP_ONBOARDING.LAUNCH_SMILE_TITLE") }}
            </h1>
          </div>
          <div class="d-flex flex-row align-item-center my-3">
            <div class="mx-1">
              <img
                :width="25"
                :src="useAsset('img/core-app-onboarding/checkmark-icon.svg')"
              />
            </div>
            <div class="fs-6">
              {{ t("CORE_APP_ONBOARDING.LAUNCH_SMILE_DESCRIPTION_1") }}
            </div>
          </div>
          <div class="d-flex flex-row align-item-center my-3">
            <div class="mx-1">
              <img
                :width="25"
                :src="useAsset('img/core-app-onboarding/checkmark-icon.svg')"
              />
            </div>
            <div class="fs-6">
              {{ t("CORE_APP_ONBOARDING.LAUNCH_SMILE_DESCRIPTION_2") }}
            </div>
          </div>
          <div class="d-flex flex-row align-item-center my-3">
            <div class="mx-1">
              <img
                :width="25"
                :src="useAsset('img/core-app-onboarding/checkmark-icon.svg')"
              />
            </div>
            <div class="fs-6">
              {{ t("CORE_APP_ONBOARDING.LAUNCH_SMILE_DESCRIPTION_3") }}
            </div>
          </div>
        </div>
      </div>

      <div class="bottom">
        <div class="my-3">
          <Button
            :text="t('CORE_APP_ONBOARDING.LAUNCH_SMILE_START_CTA')"
            :click-handler="launchPersonReg"
            id="kyc-start-button"
          />
        </div>
        <Button
          :secondary="true"
          :text="t('CORE_APP_ONBOARDING.LAUNCH_SMILE_LOGIN_CTA')"
          :click-handler="navigateToLogin"
          id="kyc-start-button-has-registration-tag"
        />
      </div>
    </div>
  </main>
  <ChatbotWrapper />
</template>

<script lang="ts" setup>
import { ref, onBeforeMount, onMounted, getCurrentInstance } from "vue";
import { useRouter } from "vue-router";
import { useI18n } from "@/apps/core-app-onboarding/composables/useI18n";
import { setLanguage } from "@/apps/core-app-onboarding/helpers/translations.helper";
import useAsset from "@/apps/core-app-onboarding/helpers/useAsset";
import Button from "@/apps/core-app-onboarding/components/Button.vue";
import ChatbotWrapper from "@/apps/core-app-onboarding/components/ChatbotWrapper.vue";
import LoadingOverlay from "@/shared-components/ui/core-app/LoadingOverlay.vue";
import logAnalyticsEvent from "@/apps/core-app-onboarding/services/google-analytics-events.service";
import { localStoragePersonUuidKey } from "@/apps/core-app-onboarding/store/core-app-onboarding.module";
import { AndroidInterface } from "@/apps/core-app-onboarding/services/android.service";
import { APIService as InStoreApiService } from "@/apps/core-app-onboarding/services/apiClient/api.service";
// CreditLine app services
import { ErrorHandler } from "@/apps/credit-line/helpers/errorHandlerWithLogger.helper";
import AnalyticEvents from "@/shared-helpers/google-analytics-events.helper";
import { sendCredolabDeviceReport } from "@/shared-services/credolab-api.service.ts";

declare const Android: AndroidInterface;

const inStoreApi = new InStoreApiService();
const { t } = useI18n();
const router = useRouter();
const isLoading = ref<boolean>(false);
const storedSessionId = ref<string>(Android.getCreditLineApplicationId());
const storedPersonUuid = ref<string | null>(
  localStorage.getItem(localStoragePersonUuidKey)
);

function hideLoader() {
  if (Android?.finishedLoading) {
    Android.finishedLoading();
  } else {
    setTimeout(() => {
      hideLoader();
    }, 1000);
  }
}

function launchPersonReg() {
  isLoading.value = true;
  inStoreApi
    .initiatePersonRegistration({
      applicationId: storedSessionId.value,
    })
    .then((res) => {
      const personRegLaunchUrl = res?.data?.launchUrl;
      if (personRegLaunchUrl) {
        window.location.href = personRegLaunchUrl;
      }
    })
    .catch((err) => {
      ErrorHandler.handleNetworkErrorAndLog(err);
      isLoading.value = false;
    });
}

function navigateToLogin() {
  logAnalyticsEvent(
    AnalyticEvents.CoreAppOnboarding.PersonRegTagLoginCTAClick,
    "CoreAppOnboarding"
  );
  router.push({
    name: "PersonRegistrationTagLoginView",
  });
}

onBeforeMount(() => {
  setLanguage(getCurrentInstance().proxy);
  inStoreApi
    .checkApplicationSource({
      applicationId: storedSessionId.value,
    })
    .then((res) => {
      console.log(res);
      // Uncomment as a safety measure for preventing at-home originations.
      // if (res.data.applicationSource === "play store") {
      //   router.push({ name: "ComingSoonView" });
      // }
    });
});

onMounted(() => {
  // Hide Android loading overlay
  hideLoader();

  // If we already have a personUuid on cache, skip KYC
  if (storedPersonUuid.value) {
    router.push({ name: "PersonRegistrationCompleteView" });
  }

  // Log Screen View into GA
  logAnalyticsEvent(
    AnalyticEvents.CoreAppOnboarding.OMViewKYCStartScreen,
    "CoreAppOnboarding"
  );

  try {
    sendCredolabDeviceReport({
      caller: "PersonRegistrationLaunchView mounted",
    });
  } catch (error) {
    console.log(error);
  }
});
</script>

<style lang="scss" scoped>
@import "@/apps/core-app-onboarding/styles/index.scss";
.top {
  padding: 0 20px;
  flex-grow: 1;
  display: flex;
  flex-direction: column;
}
.bottom {
  flex-grow: 0;
  padding: 20px 0;
}
</style>
